import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import {
  Box,
  TextField,
  Typography,
  Alert,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
} from '@mui/material'
import {
  PhotoCamera as PhotoCameraIcon,
  Business as BusinessIcon,
  Favorite as FavoriteIcon,
  Work as WorkIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material'
import {
  RoomType,
  ROOM_TYPE_LABELS,
  Gender,
  GENDER_LABELS,
  BusinessProfile,
  DatingProfile,
  JobProfile,
  BusinessProfileForm,
  DatingProfileForm,
  JobProfileForm,
} from '@/types/profile'
import { useMyProfile } from '@/contexts/ProfileContext'

// ── コンバーター ───────────────────────────────────────────
const toBusinessForm = (p: BusinessProfile): BusinessProfileForm => ({
  name: p.name,
  companyName: p.companyName,
  position: p.position,
  businessContent: p.businessContent,
  pr: p.pr ?? '',
  comment: p.comment ?? '',
  wantToMatch: p.wantToMatch ?? '',
  notWantToMatch: p.notWantToMatch ?? '',
  contactEmail: p.contactEmail ?? '',
  contactPhone: p.contactPhone ?? '',
  photoUrl: p.photoUrl ?? '',
})

const toDatingForm = (p: DatingProfile): DatingProfileForm => ({
  name: p.name,
  age: String(p.age),
  occupation: p.occupation,
  gender: p.gender,
  annualIncome: p.annualIncome,
  wantChildren: p.wantChildren ?? '',
  pr: p.pr ?? '',
  wantToMatch: p.wantToMatch ?? '',
  notWantToMatch: p.notWantToMatch ?? '',
  contactEmail: p.contactEmail ?? '',
  contactPhone: p.contactPhone ?? '',
  photoUrl: p.photoUrl ?? '',
})

const toJobForm = (p: JobProfile): JobProfileForm => ({
  name: p.name,
  companyName: p.companyName ?? '',
  lastPosition: p.lastPosition,
  education: p.education,
  schoolName: p.schoolName,
  age: p.age !== undefined ? String(p.age) : '',
  gender: p.gender ?? '',
  pr: p.pr ?? '',
  comment: p.comment ?? '',
  wantToMatch: p.wantToMatch ?? '',
  notWantToMatch: p.notWantToMatch ?? '',
  contactEmail: p.contactEmail ?? '',
  contactPhone: p.contactPhone ?? '',
  photoUrl: p.photoUrl ?? '',
})

const emptyBusinessForm: BusinessProfileForm = {
  name: '', companyName: '', position: '', businessContent: '',
  pr: '', comment: '', wantToMatch: '', notWantToMatch: '',
  contactEmail: '', contactPhone: '', photoUrl: '',
}
const emptyDatingForm: DatingProfileForm = {
  name: '', age: '', occupation: '', gender: '',
  annualIncome: '', wantChildren: '', pr: '',
  wantToMatch: '', notWantToMatch: '',
  contactEmail: '', contactPhone: '', photoUrl: '',
}
const emptyJobForm: JobProfileForm = {
  name: '', companyName: '', lastPosition: '', education: '',
  schoolName: '', age: '', gender: '', pr: '', comment: '',
  wantToMatch: '', notWantToMatch: '', contactEmail: '', contactPhone: '', photoUrl: '',
}

// ── ハンドル型 ─────────────────────────────────────────────
export interface ProfileTabFormHandle {
  submit(): Promise<boolean>
}

// ── Props ──────────────────────────────────────────────────
interface ProfileTabFormProps {
  mode?: 'register' | 'edit'
  initialRoomType?: RoomType
  initialProfile?: BusinessProfile | DatingProfile | JobProfile | null
  onValidSubmit: (profile: BusinessProfile | DatingProfile | JobProfile, pendingPhoto?: { fileBase64: string; fileName: string } | null) => void
  onRoomTypeChange?: (type: RoomType) => void
}

// ── コンポーネント ──────────────────────────────────────────
export const ProfileTabForm = forwardRef<ProfileTabFormHandle, ProfileTabFormProps>(
  ({ mode = 'register', initialRoomType = 'business', initialProfile, onValidSubmit, onRoomTypeChange }, ref) => {
    const isEditMode = mode === 'edit'
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const { myProfiles, getMyProfile } = useMyProfile()

    const [roomType, setRoomType] = useState<RoomType>(initialRoomType)
    const [error, setError] = useState<string | null>(null)
    const [photoError, setPhotoError] = useState<string | null>(null)
    const [isUploadingPhoto] = useState(false)
    const [pendingPhotos, setPendingPhotos] = useState<Record<RoomType, { fileBase64: string; fileName: string } | null>>({
      business: null,
      dating: null,
      job: null,
    })
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [businessForm, setBusinessForm] = useState<BusinessProfileForm>(() =>
      initialProfile && initialProfile.type === 'business' 
        ? toBusinessForm(initialProfile)
        : myProfiles.business 
          ? toBusinessForm(myProfiles.business) 
          : emptyBusinessForm
    )
    const [datingForm, setDatingForm] = useState<DatingProfileForm>(() =>
      initialProfile && initialProfile.type === 'dating'
        ? toDatingForm(initialProfile)
        : myProfiles.dating
          ? toDatingForm(myProfiles.dating)
          : emptyDatingForm
    )
    const [jobForm, setJobForm] = useState<JobProfileForm>(() =>
      initialProfile && initialProfile.type === 'job'
        ? toJobForm(initialProfile)
        : myProfiles.job
          ? toJobForm(myProfiles.job)
          : emptyJobForm
    )

    // 登録モードでも既存プロフィールを反映する。
    // 反映しないと、登録済みなのに空欄のフォームが出て「未登録」に見え、
    // 利用者が同じ内容を入力し直すことになる。
    useEffect(() => {
      // initialProfileがあれば優先
      if (initialProfile) {
        if (initialProfile.type === 'business') setBusinessForm(toBusinessForm(initialProfile))
        if (initialProfile.type === 'dating') setDatingForm(toDatingForm(initialProfile))
        if (initialProfile.type === 'job') setJobForm(toJobForm(initialProfile))
      } else {
        const p = getMyProfile(roomType)
        if (p) {
          if (p.type === 'business') setBusinessForm(toBusinessForm(p))
          if (p.type === 'dating') setDatingForm(toDatingForm(p))
          if (p.type === 'job') setJobForm(toJobForm(p))
        }
      }
    }, [roomType, initialProfile]) // eslint-disable-line react-hooks/exhaustive-deps

    const isRegistered = (type: RoomType) => myProfiles[type] !== null

    const getPhotoUrl = () => {
      if (roomType === 'business') return businessForm.photoUrl
      if (roomType === 'dating') return datingForm.photoUrl
      return jobForm.photoUrl
    }

    const setPhotoUrl = (url: string) => {
      if (roomType === 'business') setBusinessForm((f) => ({ ...f, photoUrl: url }))
      else if (roomType === 'dating') setDatingForm((f) => ({ ...f, photoUrl: url }))
      else setJobForm((f) => ({ ...f, photoUrl: url }))
    }

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        setPhotoError('ファイルサイズは5MB以下にしてください')
        return
      }
      setPhotoError(null)

      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        const base64Data = dataUrl.split(',')[1] || dataUrl
        // プレビュー用にdata URLをphotoUrlにセット（まだStorageにはアップロードしない）
        setPhotoUrl(dataUrl)
        setPendingPhotos((prev) => ({ ...prev, [roomType]: { fileBase64: base64Data, fileName: file.name } }))
      }
      reader.onerror = () => setPhotoError('画像の読み込みに失敗しました')
      reader.readAsDataURL(file)

      e.target.value = ''
    }

    const handleTabChange = (_: React.SyntheticEvent, newValue: RoomType) => {
      setRoomType(newValue)
      setError(null)
      onRoomTypeChange?.(newValue)
    }

    useImperativeHandle(ref, () => ({
      async submit(): Promise<boolean> {
        setError(null)
        let profile: BusinessProfile | DatingProfile | JobProfile | null = null

        const pendingPhoto = pendingPhotos[roomType] ?? null

        if (roomType === 'business') {
          if (!businessForm.name || !businessForm.companyName || !businessForm.position || !businessForm.businessContent) {
            setError('必須項目を入力してください')
            return false
          }
          const existing = myProfiles.business
          profile = {
            id: existing?.id ?? `my-business-${Date.now()}`,
            type: 'business',
            name: businessForm.name,
            companyName: businessForm.companyName,
            position: businessForm.position,
            businessContent: businessForm.businessContent,
            pr: businessForm.pr || undefined,
            comment: businessForm.comment || undefined,
            wantToMatch: businessForm.wantToMatch || undefined,
            notWantToMatch: businessForm.notWantToMatch || undefined,
            contactEmail: businessForm.contactEmail || undefined,
            contactPhone: businessForm.contactPhone || undefined,
            photoUrl: businessForm.photoUrl || undefined,
            createdAt: existing?.createdAt ?? new Date(),
          } satisfies BusinessProfile
        } else if (roomType === 'dating') {
          if (!datingForm.name || !datingForm.age || !datingForm.occupation || !datingForm.gender || !datingForm.annualIncome) {
            setError('必須項目を入力してください')
            return false
          }
          const existing = myProfiles.dating
          profile = {
            id: existing?.id ?? `my-dating-${Date.now()}`,
            type: 'dating',
            name: datingForm.name,
            age: Number(datingForm.age),
            occupation: datingForm.occupation,
            gender: datingForm.gender as Gender,
            annualIncome: datingForm.annualIncome,
            wantChildren: datingForm.wantChildren || undefined,
            pr: datingForm.pr || undefined,
            wantToMatch: datingForm.wantToMatch || undefined,
            notWantToMatch: datingForm.notWantToMatch || undefined,
            contactEmail: datingForm.contactEmail || undefined,
            contactPhone: datingForm.contactPhone || undefined,
            photoUrl: datingForm.photoUrl || undefined,
            userNumber: existing?.userNumber ?? '',
            createdAt: existing?.createdAt ?? new Date(),
          } satisfies DatingProfile
        } else if (roomType === 'job') {
          if (!jobForm.name || !jobForm.lastPosition || !jobForm.education || !jobForm.schoolName) {
            setError('必須項目を入力してください')
            return false
          }
          const existing = myProfiles.job
          profile = {
            id: existing?.id ?? `my-job-${Date.now()}`,
            type: 'job',
            name: jobForm.name,
            companyName: jobForm.companyName || undefined,
            lastPosition: jobForm.lastPosition,
            education: jobForm.education,
            schoolName: jobForm.schoolName,
            age: jobForm.age ? Number(jobForm.age) : undefined,
            gender: (jobForm.gender as Gender) || undefined,
            pr: jobForm.pr || undefined,
            comment: jobForm.comment || undefined,
            wantToMatch: jobForm.wantToMatch || undefined,
            notWantToMatch: jobForm.notWantToMatch || undefined,
            contactEmail: jobForm.contactEmail || undefined,
            contactPhone: jobForm.contactPhone || undefined,
            photoUrl: jobForm.photoUrl || undefined,
            createdAt: existing?.createdAt ?? new Date(),
          } satisfies JobProfile
        }

        if (!profile) return false
        onValidSubmit(profile, pendingPhoto)
        return true
      },
    }))

    const size = isMobile ? ('small' as const) : ('medium' as const)
    const shortLabels: Record<RoomType, string> = { business: 'ビジネス', dating: '婚活', job: '転職' }
    const tabIcons: Record<RoomType, React.ReactElement> = {
      business: <BusinessIcon />,
      dating: <FavoriteIcon />,
      job: <WorkIcon />,
    }

    const renderTab = (type: RoomType) => (
      <Tab
        key={type}
        value={type}
        icon={tabIcons[type]}
        iconPosition="start"
        sx={{ minHeight: { xs: 48, sm: 64 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        label={
          isEditMode ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                {isMobile ? shortLabels[type] : ROOM_TYPE_LABELS[type]}
              </Typography>
              {isRegistered(type) && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
              )}
            </Box>
          ) : (
            isMobile ? shortLabels[type] : ROOM_TYPE_LABELS[type]
          )
        }
      />
    )

    const renderBusinessForm = () => (
      <>
        <TextField fullWidth label="名前" value={businessForm.name} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })} />
        <TextField fullWidth label="会社名" value={businessForm.companyName} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, companyName: e.target.value })} />
        <TextField fullWidth label="役職" value={businessForm.position} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, position: e.target.value })} />
        <TextField fullWidth label="ビジネス内容" value={businessForm.businessContent} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, businessContent: e.target.value })} />
        <TextField fullWidth label="PR" value={businessForm.pr} multiline rows={4}
          placeholder="自己PRを入力してください" size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, pr: e.target.value })} />
        <TextField fullWidth label="コメント" value={businessForm.comment} multiline rows={2} size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, comment: e.target.value })} />
        <TextField fullWidth label="マッチしたい会社・業種" value={businessForm.wantToMatch} size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, wantToMatch: e.target.value })} />
        <TextField fullWidth label="マッチしたくない会社・業種" value={businessForm.notWantToMatch} size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, notWantToMatch: e.target.value })} />
        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">連絡先（非公開）</Typography>
        </Divider>
        <TextField fullWidth label="連絡先メール" type="email" value={businessForm.contactEmail} size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, contactEmail: e.target.value })} />
        <TextField fullWidth label="連絡先電話番号" value={businessForm.contactPhone} size={size} sx={{ mb: 2 }}
          onChange={(e) => setBusinessForm({ ...businessForm, contactPhone: e.target.value })} />
      </>
    )

    const renderDatingForm = () => (
      <>
        <TextField fullWidth label="名前（ニックネーム可）" value={datingForm.name} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, name: e.target.value })} />
        <TextField fullWidth label="年齢" type="number" value={datingForm.age} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, age: e.target.value })} />
        <TextField fullWidth label="職業" value={datingForm.occupation} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, occupation: e.target.value })} />
        <FormControl fullWidth required sx={{ mb: 2 }} size={size}>
          <InputLabel>性別</InputLabel>
          <Select value={datingForm.gender} label="性別"
            onChange={(e) => setDatingForm({ ...datingForm, gender: e.target.value as Gender })}>
            {Object.entries(GENDER_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField fullWidth label="年収" value={datingForm.annualIncome} required
          placeholder="例: 500万円" size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, annualIncome: e.target.value })} />
        <TextField fullWidth label="子供欲しい" value={datingForm.wantChildren}
          placeholder="例: 2人" size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, wantChildren: e.target.value })} />
        <TextField fullWidth label="PR" value={datingForm.pr} multiline rows={4}
          placeholder="自己PRを入力してください" size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, pr: e.target.value })} />
        <TextField fullWidth label="マッチしたい相手" value={datingForm.wantToMatch} size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, wantToMatch: e.target.value })} />
        <TextField fullWidth label="マッチしたくない相手" value={datingForm.notWantToMatch} size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, notWantToMatch: e.target.value })} />
        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">連絡先（非公開）</Typography>
        </Divider>
        <TextField fullWidth label="連絡先メール・LINEなど" value={datingForm.contactEmail} size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, contactEmail: e.target.value })} />
        <TextField fullWidth label="連絡先電話番号" value={datingForm.contactPhone} size={size} sx={{ mb: 2 }}
          onChange={(e) => setDatingForm({ ...datingForm, contactPhone: e.target.value })} />
      </>
    )

    const renderJobForm = () => (
      <>
        <TextField fullWidth label="名前" value={jobForm.name} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })} />
        <TextField fullWidth label="会社名または以前の勤務先" value={jobForm.companyName}
          placeholder="新卒の場合は空欄可" size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, companyName: e.target.value })} />
        <TextField fullWidth label="最終役職" value={jobForm.lastPosition} required
          placeholder='新卒の場合は「新卒」' size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, lastPosition: e.target.value })} />
        <TextField fullWidth label="最終学歴" value={jobForm.education} required
          placeholder="例: 大卒、高卒" size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, education: e.target.value })} />
        <TextField fullWidth label="最終学校名" value={jobForm.schoolName} required size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, schoolName: e.target.value })} />
        <TextField fullWidth label="年齢" type="number" value={jobForm.age} size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, age: e.target.value })} />
        <FormControl fullWidth sx={{ mb: 2 }} size={size}>
          <InputLabel>性別</InputLabel>
          <Select value={jobForm.gender} label="性別"
            onChange={(e) => setJobForm({ ...jobForm, gender: e.target.value as Gender })}>
            <MenuItem value="">選択しない</MenuItem>
            {Object.entries(GENDER_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField fullWidth label="PR" value={jobForm.pr} multiline rows={4}
          placeholder="自己PRを入力してください" size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, pr: e.target.value })} />
        <TextField fullWidth label="コメント" value={jobForm.comment} multiline rows={2}
          placeholder="例: IT業界へ転職したい" size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, comment: e.target.value })} />
        <TextField fullWidth label="マッチしたい会社・業種" value={jobForm.wantToMatch} size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, wantToMatch: e.target.value })} />
        <TextField fullWidth label="マッチしたくない会社・業種" value={jobForm.notWantToMatch} size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, notWantToMatch: e.target.value })} />
        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">連絡先（非公開）</Typography>
        </Divider>
        <TextField fullWidth label="連絡先メール" type="email" value={jobForm.contactEmail} size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, contactEmail: e.target.value })} />
        <TextField fullWidth label="連絡先電話番号" value={jobForm.contactPhone} size={size} sx={{ mb: 2 }}
          onChange={(e) => setJobForm({ ...jobForm, contactPhone: e.target.value })} />
      </>
    )

    return (
      <>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
          {isEditMode
            ? '編集したいイベントタイプを選択してください'
            : 'イベントタイプを選択してプロフィールを入力してください'}
        </Typography>

        {/* タブ */}
        <Tabs
          value={roomType}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {(['business', 'dating', 'job'] as RoomType[]).map(renderTab)}
        </Tabs>

        {/* 未登録アラート（編集モードのみ） */}
        {isEditMode && !isRegistered(roomType) && (
          <Alert severity="info" sx={{ mb: 3 }}>
            このタイプのプロフィールはまだ登録されていません。下記フォームから新規登録できます。
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* プロフィール画像アップロード */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              position: 'relative',
              cursor: 'pointer',
              borderRadius: '50%',
              '&:active': { opacity: 0.8 },
            }}
          >
            <Avatar
              src={getPhotoUrl()}
              sx={{
                width: { xs: 96, sm: 112 },
                height: { xs: 96, sm: 112 },
                fontSize: { xs: '2.5rem', sm: '3rem' },
                border: '2px dashed',
                borderColor: getPhotoUrl() ? 'transparent' : 'primary.light',
                bgcolor: getPhotoUrl() ? undefined : 'action.hover',
              }}
            >
              {isUploadingPhoto ? (
                <CircularProgress size={40} />
              ) : !getPhotoUrl() ? (
                <PhotoCameraIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
              ) : null}
            </Avatar>
            {getPhotoUrl() && (
              <Box
                sx={{
                  position: 'absolute', bottom: 0, right: 0,
                  bgcolor: 'primary.main', borderRadius: '50%',
                  width: 30, height: 30,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid white',
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 16, color: 'white' }} />
              </Box>
            )}
          </Box>
          <Typography variant="caption" color="primary" sx={{ mt: 1, fontWeight: 600 }}>
            {getPhotoUrl() ? '写真を変更' : '写真をアップロード'}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            JPG / PNG / HEIC • 最大5MB
          </Typography>
          {getPhotoUrl() && (
            <IconButton
              size="small"
              onClick={() => setPhotoUrl('')}
              sx={{ mt: 0.5, color: 'error.main', fontSize: '0.75rem', gap: 0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
              <Typography variant="caption" color="error">削除</Typography>
            </IconButton>
          )}
          {photoError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {photoError}
            </Typography>
          )}
        </Box>

        {/* フォームフィールド */}
        {roomType === 'business' && renderBusinessForm()}
        {roomType === 'dating' && renderDatingForm()}
        {roomType === 'job' && renderJobForm()}
      </>
    )
  }
)
